import { makeAutoObservable } from "mobx";

const DEFAULT_PAGE_SIZE = 5;

class DashboardOverviewStore {
  rows = [];
  total = 0;
  totalCo2eReduction = null;

  // view state
  groupBy = "organization"; // "metric" | "organization"
  viewMode = "flat"; // "flat" | "consolidated" (consolidated only valid when groupBy === "metric")
  search = "";
  page = 1;
  pageSize = DEFAULT_PAGE_SIZE;

  loading = false;
  hasLoadedOnce = false;
  requestSeq = 0;

  constructor() {
    makeAutoObservable(this);
  }

  nextRequestSeq = () => {
    this.requestSeq += 1;
    return this.requestSeq;
  };

  isStaleSeq = (seq) => seq !== this.requestSeq;

  setResult = ({ rows, total, page, pageSize, totalCo2eReduction }) => {
    this.rows = rows ?? [];
    this.total = Number(total ?? 0);
    this.page = Number(page ?? this.page);
    this.pageSize = Number(pageSize ?? this.pageSize);
    this.totalCo2eReduction = totalCo2eReduction ?? null;
    this.hasLoadedOnce = true;
  };

  setLoading = (value) => {
    this.loading = Boolean(value);
  };

  // Context-changing setters reset page to 1 so the user doesn't end up
  // on a page that no longer exists with the new filter/grouping.
  // Row shape changes between views — clear rows + flip loading so the
  // UI doesn't render stale-shape rows during the reaction's debounce.
  setGroupBy = (value) => {
    if (value !== "metric" && value !== "organization") return;
    if (value === this.groupBy) return;
    this.groupBy = value;
    this.page = 1;
    this.rows = [];
    this.total = 0;
    this.totalCo2eReduction = null;
    this.loading = true;
  };

  setViewMode = (value) => {
    if (value !== "flat" && value !== "consolidated") return;
    if (value === this.viewMode) return;
    this.viewMode = value;
    if (this.groupBy === "metric") {
      this.page = 1;
      this.loading = true;
    }
  };

  setSearch = (value) => {
    this.search = value ?? "";
    this.page = 1;
  };

  setPageSize = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 1) return;
    this.pageSize = n;
    this.page = 1;
  };

  setPage = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 1) return;
    this.page = n;
  };

  reset = () => {
    this.rows = [];
    this.total = 0;
    this.totalCo2eReduction = null;
    this.groupBy = "organization";
    this.search = "";
    this.page = 1;
    this.pageSize = DEFAULT_PAGE_SIZE;
    this.loading = false;
    this.hasLoadedOnce = false;
  };
}

export const dashboardOverviewStore = new DashboardOverviewStore();
